/**
 * 文件校验规则 — 纯函数，零副作用，可独立测试
 *
 * 阶段 1（浏览器端预检）的所有校验逻辑集中在此文件。
 * 每个函数返回 { passed: boolean, issue?: { code, message, severity } }
 */

// =============================================================================
// 常量
// =============================================================================

const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".xlsx", ".docx"];
const ALLOWED_MIME_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const MAX_FILE_SIZE_MB = 60;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const PDF_PAGE_LIMIT = 10;
const WORD_CHAR_LIMIT_SOFT = 25000;
const WORD_CHAR_LIMIT_HARD = 50000;

// =============================================================================
// 单项校验
// =============================================================================

/**
 * 文件类型校验
 */
export function checkFileType(file) {
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
        return {
            passed: false,
            issue: {
                code: "INVALID_TYPE",
                message: `不支持的文件类型 "${ext}"，仅支持: ${ALLOWED_EXTENSIONS.join(", ")}`,
                severity: "error",
            },
        };
    }
    return { passed: true };
}

/**
 * 文件大小校验
 */
export function checkFileSize(file) {
    if (file.size > MAX_FILE_SIZE_BYTES) {
        const sizeMB = (file.size / 1024 / 1024).toFixed(1);
        return {
            passed: false,
            issue: {
                code: "TOO_LARGE",
                message: `文件大小 ${sizeMB}MB 超过限制 ${MAX_FILE_SIZE_MB}MB`,
                severity: "error",
            },
        };
    }
    return { passed: true };
}

/**
 * 文件名重复检测（与暂存列表比对）
 */
export function checkFilenameDuplicate(file, existingNames) {
    if (existingNames.includes(file.name)) {
        return {
            passed: false,
            issue: {
                code: "DUPLICATE_NAME",
                message: `文件 "${file.name}" 已在列表中`,
                severity: "warning",
            },
        };
    }
    return { passed: true };
}

/**
 * PDF 页数校验
 */
export function checkPageCount(pageCount) {
    if (pageCount == null) return { passed: true };

    if (pageCount > PDF_PAGE_LIMIT) {
        return {
            passed: true, // 不阻断，但标记需要拆分
            needsSplit: true,
            issue: {
                code: "NEEDS_SPLIT",
                message: `PDF 共 ${pageCount} 页，超过 ${PDF_PAGE_LIMIT} 页限制，建议拆分`,
                severity: "warning",
            },
        };
    }
    return { passed: true };
}

/**
 * Word 字符数校验
 */
export function checkCharCount(charCount) {
    if (charCount == null) return { passed: true };

    if (charCount > WORD_CHAR_LIMIT_HARD) {
        return {
            passed: false,
            issue: {
                code: "CHAR_LIMIT_HARD",
                message: `Word 文件包含 ${charCount.toLocaleString()} 字符，超过硬限制 ${WORD_CHAR_LIMIT_HARD.toLocaleString()}`,
                severity: "error",
            },
        };
    }
    if (charCount > WORD_CHAR_LIMIT_SOFT) {
        return {
            passed: true,
            issue: {
                code: "CHAR_LIMIT_SOFT",
                message: `Word 文件包含 ${charCount.toLocaleString()} 字符，接近上限（建议 < ${WORD_CHAR_LIMIT_SOFT.toLocaleString()}）`,
                severity: "warning",
            },
        };
    }
    return { passed: true };
}

// =============================================================================
// 元数据提取（异步）
// =============================================================================

/**
 * 本地提取 PDF 页数（使用 pdf.js）
 */
export async function extractPdfPageCount(file) {
    try {
        const pdfjsLib = await import("pdfjs-dist/build/pdf.mjs");

        // 配置 worker
        if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
            pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;
        }

        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        return pdf.numPages;
    } catch (err) {
        console.warn(`[fileValidation] PDF 页数提取失败 (${file.name}):`, err);
        return null;
    }
}

/**
 * 本地提取 Word 字符数（使用 mammoth）
 */
export async function extractWordCharCount(file) {
    try {
        const mammoth = await import("mammoth");
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        return result.value.length;
    } catch (err) {
        console.warn(`[fileValidation] Word 字符数提取失败 (${file.name}):`, err);
        return null;
    }
}

// =============================================================================
// 编排函数：综合校验
// =============================================================================

/**
 * 对单个文件执行全套阶段 1 校验
 *
 * @param {File} file - 原始 File 对象
 * @param {string[]} existingNames - 暂存列表中已有的文件名
 * @returns {Promise<{
 *   phase: "passed" | "blocked" | "needs_split" | "warning",
 *   issues: Array<{code, message, severity}>,
 *   metadata: { pageCount?: number, charCount?: number }
 * }>}
 */
export async function validateFile(file, existingNames = []) {
    const issues = [];
    let blocked = false;
    let needsSplit = false;
    const metadata = {};

    // 1. 同步校验
    const typeCheck = checkFileType(file);
    if (!typeCheck.passed) {
        return { phase: "blocked", issues: [typeCheck.issue], metadata };
    }

    const sizeCheck = checkFileSize(file);
    if (!sizeCheck.passed) {
        return { phase: "blocked", issues: [sizeCheck.issue], metadata };
    }

    const dupCheck = checkFilenameDuplicate(file, existingNames);
    if (!dupCheck.passed && dupCheck.issue) {
        issues.push(dupCheck.issue);
    }

    // 2. 异步元数据提取 + 校验
    const ext = file.name.split(".").pop()?.toLowerCase();

    if (ext === "pdf") {
        const pageCount = await extractPdfPageCount(file);
        metadata.pageCount = pageCount;

        if (pageCount != null) {
            const pageCheck = checkPageCount(pageCount);
            if (pageCheck.needsSplit) {
                needsSplit = true;
            }
            if (pageCheck.issue) {
                issues.push(pageCheck.issue);
            }
        }
    } else if (ext === "docx") {
        const charCount = await extractWordCharCount(file);
        metadata.charCount = charCount;

        if (charCount != null) {
            const charCheck = checkCharCount(charCount);
            if (!charCheck.passed) {
                blocked = true;
            }
            if (charCheck.issue) {
                issues.push(charCheck.issue);
            }
        }
    }

    // 3. 确定最终 phase
    let phase = "passed";
    if (blocked) {
        phase = "blocked";
    } else if (needsSplit) {
        phase = "needs_split";
    } else if (issues.length > 0) {
        phase = "warning";
    }

    return { phase, issues, metadata };
}

// 导出常量供外部使用
export const VALIDATION_CONSTANTS = {
    ALLOWED_EXTENSIONS,
    ALLOWED_MIME_TYPES,
    MAX_FILE_SIZE_MB,
    PDF_PAGE_LIMIT,
    WORD_CHAR_LIMIT_SOFT,
    WORD_CHAR_LIMIT_HARD,
};
